'use client';
import { startTransition, useContext, useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import toast from 'react-hot-toast';
import { SessionContext } from '@benzinga/session-context';
import { useWatchlistContext } from '@benzinga/user-context';
import { WatchlistManager } from '@benzinga/watchlist-manager';
import { Modal, Spinner } from '@benzinga/core-ui';
import { isCreateWatchlistAction } from '../portfolioActionUtils';
import { PortfolioModalHeader } from './PortfolioModalHeader';
import {
  portfolioModalBodyClass,
  portfolioModalContainerStyle,
  portfolioModalFooterClass,
  portfolioModalShellClass,
} from './modalStyles';

export const CreateWatchlist = () => {
  const { showCreateModal, setShowCreateModal, setSelectedWatchlist } = useWatchlistContext();
  const session = useContext(SessionContext);
  const watchlistManager = session.getManager(WatchlistManager);

  const [watchlistName, setWatchlistName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const hasProcessedAction = useRef(false);

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const action = searchParams?.get('action');

  useEffect(() => {
    if (isCreateWatchlistAction(action) && !showCreateModal && !hasProcessedAction.current) {
      if (searchParams) {
        hasProcessedAction.current = true;
        const params = new URLSearchParams(searchParams.toString());
        params.delete('action');
        const newSearch = params.toString();
        const newUrl = `${pathname}${newSearch ? `?${newSearch}` : ''}`;
        window.history.replaceState({}, '', newUrl);

        startTransition(() => {
          setShowCreateModal(true);
        });
      }
    }
  }, [action, searchParams, pathname, setShowCreateModal, showCreateModal]);

  const handleCreateWatchlist = async (name: string) => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    setIsLoading(true);
    try {
      const result = await watchlistManager.createWatchlist(trimmedName);
      if (result.err) {
        toast.error('Unable to create watchlist. Please try again.');
        return;
      }
      setSelectedWatchlist(result.ok);
      toast.success(`${trimmedName} created`);
      setShowCreateModal(false);
      setWatchlistName('');
    } catch (error) {
      console.error('Error creating watchlist:', error);
      toast.error('Error creating watchlist. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const cancelCreateWatchlist = () => {
    setShowCreateModal(false);
    setWatchlistName('');
  };

  return (
    <Modal
      visible={showCreateModal}
      onClose={cancelCreateWatchlist}
      disableFullScreenButton={true}
      hideTitle={true}
      fullScreen={true}
      size={{ manual: { width: '440px', maxWidth: 'calc(100vw - 32px)', height: 'auto' } }}
      containerStyle={portfolioModalContainerStyle}
    >
      <div className={portfolioModalShellClass}>
        <PortfolioModalHeader title="Create a new watchlist" onClose={cancelCreateWatchlist} />
        <div className={portfolioModalBodyClass}>
          <label className="mb-2 block text-sm text-slate-600" htmlFor="new-watchlist-name">
            Watchlist name
          </label>
          <input
            id="new-watchlist-name"
            className="min-h-10 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-bzblue-500 focus:outline-none focus:ring-2 focus:ring-bzblue-100"
            type="text"
            maxLength={80}
            placeholder="e.g. AI leaders"
            value={watchlistName}
            disabled={isLoading}
            onChange={event => setWatchlistName(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') handleCreateWatchlist(watchlistName);
            }}
          />
          <p className="mt-2 text-xs text-slate-500">You can add stocks and set alerts after creating it.</p>
        </div>

        <div className={portfolioModalFooterClass}>
          <button
            type="button"
            onClick={cancelCreateWatchlist}
            disabled={isLoading}
            className="min-h-10 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleCreateWatchlist(watchlistName)}
            disabled={isLoading || !watchlistName.trim()}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-bzblue-700 px-4 text-sm font-bold text-white hover:bg-bzblue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? <Spinner size={16} /> : 'Create watchlist'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
